import * as ReactLeaflet from "react-leaflet";
export type LatLng = ReactLeaflet.LatLng;
export type Point = ReactLeaflet.Point;
export type LatLngBounds = ReactLeaflet.LatLngBounds;

export const OWN_POSTION_MARKER_ID = "OWN_POSTION_MARKER_ID";

export enum WebViewLeafletEvents {
  MAP_COMPONENT_MOUNTED = "MAP_COMPONENT_MOUNTED",
  MAP_READY = "MAP_READY",
  DOCUMENT_EVENT_LISTENER_ADDED = "DOCUMENT_EVENT_LISTENER_ADDED",
  WINDOW_EVENT_LISTENER_ADDED = "WINDOW_EVENT_LISTENER_ADDED",
  UNABLE_TO_ADD_EVENT_LISTENER = "UNABLE_TO_ADD_EVENT_LISTENER",
  DOCUMENT_EVENT_LISTENER_REMOVED = "DOCUMENT_EVENT_LISTENER_REMOVED",
  WINDOW_EVENT_LISTENER_REMOVED = "WINDOW_EVENT_LISTENER_REMOVED",
  ON_MOVE_END = "onMoveEnd",
  ON_MOVE_START = "onMoveStart",
  ON_MOVE = "onMove",
  ON_RESIZE = "onResize",
  ON_UNLOAD = "onUnload",
  ON_VIEW_RESET = "onViewReset",
  ON_ZOOM_END = "onZoomEnd",
  ON_ZOOM_LEVELS_CHANGE = "onZoomLevelsChange",
  ON_ZOOM_START = "onZoomStart",
  ON_ZOOM = "onZoom",
  ON_MAP_TOUCHED = "onMapClicked",
  ON_MAP_MARKER_CLICKED = "onMapMarkerClicked",
  ON_DRAG_START = "onDragStart",
  //  ON_MAP_SHAPE_CLICKED = "onMapShapeClicked" cannot click on shapes yet
}

export enum AnimationType {
  BOUNCE = "bounce",
  FADE = "fade",
  PULSE = "pulse",
  JUMP = "jump",
  SPIN = "spin",
  WAGGLE = "waggle"
}

export enum MapLayerType {
  IMAGE_LAYER = "ImageOverlay",
  TILE_LAYER = "TileLayer",
  VECTOR_LAYER = "VectorLayer",
  VIDEO_LAYER = "VideoOverlay",
  WMS_TILE_LAYER = "WMSTileLayer"
}

export enum MapShapeType {
  CIRCLE = "Circle",
  CIRCLE_MARKER = "CircleMarker",
  POLYLINE = "Polyline",
  POLYGON = "Polygon",
  RECTANGLE = "Rectangle"
}

export const INFINITE_ANIMATION_ITERATIONS: string = "infinite";

export enum AnimationDirection {
  NORMAL = "nomal",
  REVERSE = "reverse",
  ALTERNATE = "alternate",
  ALTERNATE_REVERSE = "alternate-reverse"
}
export interface MapMarkerAnimation {
  type: AnimationType;
  duration?: number;
  delay?: number;
  direction?: AnimationDirection;
  iterationCount?: number | typeof INFINITE_ANIMATION_ITERATIONS;
}

export interface MapMarker {
  animation?: MapMarkerAnimation;
  position: LatLng;
  divIcon?: L.DivIcon;
  icon: any;
  iconAnchor?: Point;
  id?: string;
  size?: Point;
  title?: string;
  badgeLabel?: string;
  iconPopup?: string;
  badgeColor?: string;
  badgeBgColor?: string;
  rotationAngle?: number;
  rotationOrigin?: string;
}

export interface MapEventMessage {
  event?: any;
  msg?: string;
  error?: string;
  payload?: any;
  /**
   * 2026-10-10 : ce que cette page sait faire, joint à `MAP_READY`. L'application s'en sert pour
   * choisir entre le repère animé par la page (`gpsActive`) et son ancien repère.
   */
  features?: { [name: string]: boolean };
}

/**
 * 2026-10-10 : repère du guidage animé par la page (message `gpsActive`). L'application envoie
 * UNE position par relevé GPS ; la page fait glisser le repère (et la carte, si `follow`) de la
 * position affichée à celle-ci, à la cadence d'affichage. `null` retire le repère.
 */
export interface GpsActiveMessage {
  lat: number;
  lng: number;
  /** Cap en degrés (0 = nord), appliqué par une rotation de l'icône. */
  bearing?: number;
  /** La carte suit le repère (vrai par défaut). */
  follow?: boolean;
  /** Adresse de l'image du repère. */
  icon?: string;
  /** Largeur et hauteur de l'icône en pixels (32 par 32 par défaut). */
  size?: [number, number];
  /**
   * 2026-10-11 : durée maximale (ms) pendant laquelle le repère continue sur sa lancée, à la
   * même vitesse, si la position suivante tarde (0 = jamais, pour un traceur).
   */
  extrapolateMs?: number;
  /**
   * 2026-10-11 : vitesse du véhicule en m/s d'après le GPS (lissée par l'application), utilisée
   * pour la prolongation ; sans elle, la vitesse est déduite du dernier glissement.
   */
  speedMps?: number;
  /**
   * 2026-10-11 : cadence attendue des positions (ms). Si donnée, chaque glissement dure ce
   * temps (le repère avance à l'allure réelle après un retard, au lieu de ralentir) ; sinon il
   * dure le temps écoulé depuis la position précédente (traceur).
   */
  cadenceMs?: number;
  /**
   * 2026-10-12 : carte dans le sens de la marche. Pendant le suivi (`follow`), la carte tourne
   * pour que le cap du repère soit en haut de l'écran ; sans suivi, ou sans ce champ, elle reste
   * nord en haut. L'application ne l'envoie que si la page annonce `features.headingUp`.
   */
  headingUp?: boolean;
}

export interface MapLayer {
  attribution?: string;
  baseLayer?: boolean;
  baseLayerIsChecked?: boolean;
  baseLayerName?: string;
  bounds?: LatLngBounds;
  id?: string;
  layerType?: MapLayerType;
  /** Dernier niveau de zoom que le serveur de tuiles sait servir. */
  maxNativeZoom?: number;
  /** Dernier niveau de zoom où la couche est affichée (tuiles agrandies au-delà de `maxNativeZoom`). */
  maxZoom?: number;
  opacity?: number;
  pane?: string;
  subLayer?: string;
  url?: string;
  zIndex?: number;
}

export interface MapShape {
  bounds?: LatLng[];
  center?: LatLng;
  color?: string;
  id?: string;
  positions?: LatLng[] | LatLng[][];
  radius?: number;
  shapeType: MapShapeType;
}

export interface MapStartupMessage {
  mapLayers?: MapLayer[];
  mapMarkers?: MapMarker[];
  mapShapes?: MapShape[];
  mapCenterPosition?: LatLng;
  ownPositionMarker?: OwnPositionMarker;
  zoom?: number;
}

export type WebviewLeafletMessagePayload = {
  bounds?: LatLngBounds;
  mapCenterPosition: LatLng;
  mapMarkerID?: string;
  touchLatLng?: LatLng;
  zoom?: number;
};

export interface WebviewLeafletMessage {
  event?: any;
  msg?: string;
  error?: string;
  payload?: WebviewLeafletMessagePayload;
}

export type OwnPositionMarker = {
  animation: MapMarkerAnimation;
  id?: string;
  icon: string;
  position: LatLng;
  size: Point;
};
